import { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Image, Loader2, Download, Scissors, RotateCcw, Pencil } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useImageUpload } from './chart-converter/useImageUpload';

const WS_URL = import.meta.env.VITE_LABS_WS_URL || 'wss://uknjkyctd3.execute-api.us-east-1.amazonaws.com/prod';

// [<]---selected range---[>] drag slicer

// Inline edit component: pencil icon on hover, switches to input on click
const EditableText = ({ value, onChange, className = '' }) => {
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    setHovered(false);
    if (draft.trim() && draft !== value) onChange(draft.trim());
    else setDraft(value);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
        className={`bg-transparent border-b border-violet-400 outline-none text-center ${className}`}
        style={{ minWidth: 60, width: `${Math.max(draft.length, 4)}ch` }}
      />
    );
  }

  return (
    <span
      className={`relative inline-flex items-center gap-1 cursor-pointer group ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setEditing(true)}
    >
      {value}
      {hovered && <Pencil className="w-3 h-3 text-text-400 opacity-70" />}
    </span>
  );
};

const TimelineSlicer = ({ sliceRange, onChange, xLabels }) => {
  const trackRef = useRef(null);
  const dragging = useRef(null);

  const getPct = (clientX) => {
    const rect = trackRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !trackRef.current) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const pct = getPct(clientX);
      if (dragging.current === 'start') {
        onChange([Math.min(pct, sliceRange[1] - 2), sliceRange[1]]);
      } else {
        onChange([sliceRange[0], Math.max(pct, sliceRange[0] + 2)]);
      }
    };
    const onUp = () => { dragging.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [sliceRange, onChange]);

  const s = sliceRange[0];
  const e = sliceRange[1];

  return (
    <div className="space-y-2 select-none">
      {/* Percentage display */}
      <div className="flex justify-between text-xs font-mono px-1">
        <span className="text-violet-400">{Math.round(s)}%</span>
        <span className="text-violet-400">{Math.round(e)}%</span>
      </div>
      {/* Track */}
      <div ref={trackRef} className="relative h-8 flex items-center">
        <div className="absolute inset-x-0 h-1.5 rounded-full bg-bg-300/50" />
        <div
          className="absolute h-1.5 rounded-full bg-violet-500/60"
          style={{ left: s + '%', width: (e - s) + '%' }}
        />
        {/* Left handle */}
        <div
          className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-bg-100 border border-bg-300/60 shadow cursor-ew-resize z-10 hover:border-violet-400 transition-colors"
          style={{ left: s + '%', transform: 'translateX(-50%)' }}
          onMouseDown={(ev) => { ev.preventDefault(); dragging.current = 'start'; }}
          onTouchStart={(ev) => { ev.preventDefault(); dragging.current = 'start'; }}
        >
          <svg width="8" height="10" viewBox="0 0 8 10" fill="none">
            <path d="M6 1L2 5L6 9" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        {/* Right handle */}
        <div
          className="absolute flex items-center justify-center w-6 h-6 rounded-full bg-bg-100 border border-bg-300/60 shadow cursor-ew-resize z-10 hover:border-violet-400 transition-colors"
          style={{ left: e + '%', transform: 'translateX(-50%)' }}
          onMouseDown={(ev) => { ev.preventDefault(); dragging.current = 'end'; }}
          onTouchStart={(ev) => { ev.preventDefault(); dragging.current = 'end'; }}
        >
          <svg width="8" height="10" viewBox="0 0 8 10" fill="none">
            <path d="M2 1L6 5L2 9" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      {/* x labels */}
      {xLabels.length > 0 && (
        <div className="relative h-4">
          {xLabels.map((label, i) => {
            const pct = xLabels.length === 1 ? 50 : (i / (xLabels.length - 1)) * 100;
            return (
              <span key={i} className="absolute text-[10px] text-text-400 whitespace-nowrap" style={{ left: pct + '%', transform: 'translateX(-50%)' }}>
                {label}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ChartSlicer = ({ onClose }) => {
  const upload = useImageUpload();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSeriesName, setEditSeriesName] = useState('');
  const [sliceRange, setSliceRange] = useState([0, 100]);
  const wsRef = useRef(null);
  const chartRef = useRef(null);

  const [useFullAxis, setUseFullAxis] = useState(false);

  const getSlicedData = () => {
    if (!chartData?.series?.length) return [];
    const allData = chartData.series[0].data;
    const total = allData.length;
    const startIdx = Math.round((sliceRange[0] / 100) * (total - 1));
    const endIdx = Math.round((sliceRange[1] / 100) * (total - 1));

    if (!useFullAxis) {
      return allData.slice(startIdx, endIdx + 1).map(d => ({
        x: d.x,
        col: d.col,
        [editSeriesName]: d.y,
      }));
    }

    // Keep full x-axis frame, fill values only for selected range
    const xLabels = chartData.xLabels || [];
    if (xLabels.length < 2) {
      // No x labels, use regular slice
      return allData.slice(startIdx, endIdx + 1).map(d => ({
        x: d.x, col: d.col, [editSeriesName]: d.y,
      }));
    }

    // Create empty frame from all x labels, fill values only for sliced range
    const slicedSet = new Set();
    for (let i = startIdx; i <= endIdx; i++) {
      slicedSet.add(i);
    }

    // Map data by x label
    return xLabels.map((label, i) => {
      // Find data points matching this label
      const matchingPoints = allData.filter((d, idx) => d.x === label && slicedSet.has(idx));
      if (matchingPoints.length > 0) {
        const avg = matchingPoints.reduce((sum, p) => sum + p.y, 0) / matchingPoints.length;
        return { x: label, [editSeriesName]: Math.round(avg * 100) / 100 };
      }
      return { x: label, [editSeriesName]: undefined };
    });
  };



  const handleAnalyze = () => {
    console.log('[ChartSlicer] handleAnalyze called', { s3ImageUrl: upload.s3ImageUrl, previewUrl: upload.previewUrl, isUploading: upload.isUploading });
    const imageUrl = upload.s3ImageUrl || upload.previewUrl;
    if (!imageUrl) { toast.error('Please upload an image first'); return; }
    if (upload.isUploading) { toast('Please wait for the upload to complete', { icon: '⏳' }); return; }
    setIsAnalyzing(true);
    setChartData(null);

    console.log('[ChartSlicer] Connecting to WS:', WS_URL);
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    let completed = false;

    ws.onopen = () => {
      console.log('[ChartSlicer] WS connected, sending request');
      ws.send(JSON.stringify({ action: 'sendMessage', inpaintAction: 'chart_slice', imageUrl }));
    };
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      console.log('[ChartSlicer] WS message:', msg.type, msg);
      if (msg.type === 'complete' && msg.visualData?.type === 'chart_slice_result') {
        completed = true;
        setChartData(msg.visualData.data);
        setEditTitle(msg.visualData.data.title || '');
        setEditSeriesName(msg.visualData.data.series?.[0]?.name || 'Data');
        setSliceRange([0, 100]);
        setIsAnalyzing(false);
        ws.close();
        toast.success('Chart data extraction complete');
      } else if (msg.type === 'error') {
        completed = true;
        toast.error(msg.error || 'Analysis failed');
        setIsAnalyzing(false);
        ws.close();
      }
    };
    ws.onerror = (e) => {
      console.error('[ChartSlicer] WS error:', e);
      completed = true;
      toast.error('A connection error occurred');
      setIsAnalyzing(false);
    };
    ws.onclose = (e) => {
      console.log('[ChartSlicer] WS closed:', e.code, e.reason, 'completed:', completed);
      if (!completed) { setIsAnalyzing(false); toast.error('Server connection lost'); }
    };
  };

  const handleDownload = useCallback(() => {
    if (!chartRef.current) return;
    import('html2canvas').then(({ default: html2canvas }) => {
      html2canvas(chartRef.current, { backgroundColor: '#ffffff', scale: 2 }).then(canvas => {
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = 'chart_sliced.png';
        a.click();
        toast.success('Download complete');
      });
    });
  }, []);

  const slicedData = getSlicedData();
  const colors = chartData?.series?.map(s => s.color || '#6366f1') || [];

  // Determine if x-axis labels are meaningful (dates, text shown / index-like decimals hidden)
  const showXLabels = (() => {
    if (!slicedData.length) return false;
    const samples = slicedData.slice(0, Math.min(10, slicedData.length));
    const values = samples.map(d => d.x);
    const allNumeric = values.every(v => typeof v === 'number' || (typeof v === 'string' && /^-?\d*\.?\d+$/.test(v.trim())));
    if (allNumeric) {
      const nums = values.map(Number);
      if (nums.every(n => n >= 0 && n <= 1)) return false;
      const allIntegers = nums.every(n => Number.isInteger(n));
      const isSequential = allIntegers && nums.every((n, i) => i === 0 || n === nums[i - 1] + 1);
      if (isSequential) return false;
    }
    return true;
  })();

  const renderChart = () => {
    if (!slicedData.length || !chartData) return null;
    const hasData = slicedData.some(d => d[editSeriesName] !== undefined);
    if (!hasData && !useFullAxis) return null;
    const seriesNames = [editSeriesName];
    const type = chartData.chartType || 'line';
    const commonProps = { data: slicedData, margin: { top: 5, right: 15, left: 5, bottom: 5 } };
    // Always use original chart's y range
    const yDomain = chartData.yMin !== undefined && chartData.yMax !== undefined
      ? [chartData.yMin, chartData.yMax] : ['auto', 'auto'];
    const lineColor = colors[0] || '#6366f1';
    const axes = (
      <>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="x"
          tick={showXLabels ? { fontSize: 10, fill: '#888' } : false}
          tickLine={true}
          axisLine={{ stroke: '#ccc' }}
          angle={showXLabels ? -35 : 0}
          textAnchor={showXLabels ? 'end' : 'middle'}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 10, fill: '#888' }}
          domain={yDomain}
          axisLine={{ stroke: '#ccc' }}
          tickFormatter={(v) => {
            if (Math.abs(v) >= 10000) return (v / 10000).toFixed(1) + 'k';
            if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + 'k';
            return v;
          }}
        />
        <Tooltip
          formatter={(val) => val !== undefined ? [typeof val === 'number' ? val.toLocaleString() : val, seriesNames[0]] : ['—', seriesNames[0]]}
          labelFormatter={(label) => label}
        />
        <Legend content={() => null} />
      </>
    );
    if (type === 'bar') return (
      <BarChart {...commonProps}>{axes}{seriesNames.map((name, i) => <Bar key={name} dataKey={name} fill={lineColor} radius={[3, 3, 0, 0]} />)}</BarChart>
    );
    if (type === 'area') return (
      <AreaChart {...commonProps}>{axes}{seriesNames.map((name, i) => <Area key={name} type="monotone" dataKey={name} stroke={lineColor} fill={lineColor + '33'} strokeWidth={2} dot={false} connectNulls={false} />)}</AreaChart>
    );
    return (
      <LineChart {...commonProps}>{axes}{seriesNames.map((name, i) => <Line key={name} type="monotone" dataKey={name} stroke={lineColor} strokeWidth={2} dot={false} connectNulls={false} />)}</LineChart>
    );
  };

  const totalPoints = chartData?.series?.[0]?.data?.length || 0;
  const startIdx = Math.round((sliceRange[0] / 100) * (totalPoints - 1));
  const endIdx = Math.round((sliceRange[1] / 100) * (totalPoints - 1));
  const xLabels = chartData?.xLabels || [];
  const nLabels = xLabels.length;
  const startLabel = nLabels >= 2 ? xLabels[Math.round((startIdx / (totalPoints - 1)) * (nLabels - 1))] : (xLabels[0] || '');
  const endLabel = nLabels >= 2 ? xLabels[Math.round((endIdx / (totalPoints - 1)) * (nLabels - 1))] : (xLabels[nLabels - 1] || '');

  return (
    <motion.div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-bg-100 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 pt-4 pb-3 border-b border-bg-300/30 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">✂️</span>
              <h2 className="text-base font-semibold text-text-100">Chart Slicer</h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-bg-300/50 text-text-400 hover:text-text-200 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-text-400">Upload a chart image and AI will extract the data, then redraw only the range you want to slice.</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div
            className={`relative rounded-xl overflow-hidden transition-all ${
              upload.previewUrl
                ? 'bg-[#E8E8ED] dark:bg-[#1a1a2e] border border-bg-300/40'
                : 'border-2 border-dashed border-bg-300/50 hover:border-violet-500/50 hover:bg-violet-500/5 cursor-pointer p-8'
            }`}
            onDrop={upload.handleDrop}
            onDragOver={upload.handleDragOver}
            onClick={() => !upload.previewUrl && upload.fileInputRef.current?.click()}
          >
            <input ref={upload.fileInputRef} type="file" accept="image/*" onChange={upload.handleFileSelect} className="hidden" />
            {upload.previewUrl ? (
              <img src={upload.previewUrl} alt="Uploaded chart" className="w-full max-h-64 object-contain" />
            ) : (
              <div className="space-y-3 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-bg-200/50 flex items-center justify-center">
                  <Image className="w-7 h-7 text-text-400" />
                </div>
                <div>
                  <p className="text-text-200 font-medium">Upload a chart image</p>
                  <p className="text-sm text-text-400 mt-1">Click or drag a file here</p>
                </div>
              </div>
            )}
            {upload.isUploading && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <div className="flex items-center gap-2 text-white"><Loader2 className="w-5 h-5 animate-spin" /><span>Uploading...</span></div>
              </div>
            )}
          </div>

          {chartData && (
            <div className="space-y-4">
              <div className="bg-bg-200/40 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between text-xs text-text-400">
                  <span>Select Range</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setUseFullAxis(v => !v)}
                      className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${useFullAxis ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30' : 'bg-bg-300/40 text-text-500 border border-bg-300/30 hover:text-text-300'}`}
                    >
                      {useFullAxis ? 'Full Axis ON' : 'Full Axis OFF'}
                    </button>
                    <span className="font-mono text-text-300">
                      {startLabel && endLabel ? `${startLabel} → ${endLabel} (${useFullAxis ? slicedData.filter(d => d[editSeriesName] !== undefined).length : slicedData.length} points)` : `${useFullAxis ? slicedData.filter(d => d[editSeriesName] !== undefined).length : slicedData.length} points`}
                    </span>
                  </div>
                </div>
                <TimelineSlicer
                  sliceRange={sliceRange}
                  onChange={setSliceRange}
                  xLabels={xLabels}
                />
              </div>

              <div ref={chartRef} className="bg-white dark:bg-bg-200 rounded-xl p-5">
                {editTitle && (
                  <p className="text-sm font-medium text-text-200 mb-3 text-center">
                    <EditableText value={editTitle} onChange={setEditTitle} className="text-sm font-medium text-text-200" />
                  </p>
                )}
                <ResponsiveContainer width="100%" height={320}>
                  {renderChart()}
                </ResponsiveContainer>
                <div className="flex justify-center mt-2">
                  <span className="inline-flex items-center gap-1.5 text-xs text-text-300">
                    <span className="w-3 h-0.5 rounded" style={{ backgroundColor: colors[0] || '#6366f1' }} />
                    <EditableText value={editSeriesName} onChange={setEditSeriesName} className="text-xs text-text-300" />
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-bg-300/30 bg-bg-200/20">
          {chartData ? (
            <>
              <button onClick={() => { upload.resetUpload(); setChartData(null); }} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors">
                <Image className="w-4 h-4" /><span>Different Image</span>
              </button>
              <button onClick={() => setChartData(null)} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors">
                <RotateCcw className="w-4 h-4" /><span>Re-analyze</span>
              </button>
              <button onClick={handleDownload} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium bg-green-500 text-white hover:bg-green-600 rounded-xl transition-all">
                <Download className="w-4 h-4" /><span>Download</span>
              </button>
            </>
          ) : (
            <>
              <button onClick={() => { upload.resetUpload(); setChartData(null); }} disabled={!upload.previewUrl} className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-300/50 rounded-xl border border-text-300/25 transition-colors disabled:opacity-50">
                <Image className="w-4 h-4" /><span>Different Image</span>
              </button>
              <button
                onClick={handleAnalyze}
                disabled={!upload.previewUrl || upload.isUploading || isAnalyzing}
                className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium rounded-xl transition-all ${upload.previewUrl && !upload.isUploading && !isAnalyzing ? 'bg-text-200 dark:bg-text-300 text-bg-100 hover:opacity-90' : 'bg-bg-300/50 text-text-500 cursor-not-allowed'}`}
              >
                {upload.isUploading ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Uploading...</span></> : isAnalyzing ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Analyzing...</span></> : <><Scissors className="w-4 h-4" /><span>Extract Data</span></>}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ChartSlicer;
